import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatCurrency, formatDate, formatDateTime, generateBarcode } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Package, FolderOpen, ClipboardList, Truck, Plus, Search, Edit, Trash2,
  Barcode, AlertTriangle, Printer, RefreshCw, ArrowUp, ArrowDown,
  Phone, Mail, MapPin, CreditCard, Camera, X, ScanLine, Download, Upload, FileSpreadsheet
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useStore } from '../context/StoreContext';
import * as XLSX from 'xlsx';

function BarcodeDisplay({ code }) {
  const svgRef = useRef(null);
  useEffect(() => {
    if (svgRef.current && code) {
      try {
        JsBarcode(svgRef.current, code, { format: 'CODE128', width: 2, height: 50, displayValue: true, fontSize: 14, margin: 10 });
      } catch (e) { console.error('Barcode error:', e); }
    }
  }, [code]);
  return <svg ref={svgRef}></svg>;
}

export default function Magazina() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);

  // Store context
  const { currentStore, stores, canSwitchStores } = useStore();

  // Helper to get store name by id
  const getStoreName = (storeId) => {
    const store = stores?.find(s => s.id === storeId);
    return store?.name || '-';
  };

  // Show store column when viewing all stores
  const showStoreColumn = canSwitchStores && !currentStore;

  // Shared data
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [adjustments, setAdjustments] = useState([]);

  // Search states
  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [inventorySearch, setInventorySearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Modal states
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isScannedProductOpen, setIsScannedProductOpen] = useState(false);

  // Scanner refs
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);

  // Selected items
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Forms
  const [productForm, setProductForm] = useState({ name: '', barcode: '', category_id: '', purchase_price: 0, sale_price: 0, stock: 0, min_stock: 5, description: '' });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [supplierForm, setSupplierForm] = useState({ name: '', phone: '', email: '', address: '', debt: 0 });
  const [newStock, setNewStock] = useState(0);
  const [reason, setReason] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [labelCount, setLabelCount] = useState(1);

  // CSV Import state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // CSV Export function
  const exportProductsCSV = () => {
    const storeName = currentStore?.name || 'Te_Gjitha_Dyqanet';
    const csvData = products.map(p => ({
      'Emri': p.name,
      'Barkodi': p.barcode || '',
      'Kategoria': categories.find(c => c.id === p.category_id)?.name || '',
      'Çmimi Blerjes': p.purchase_price,
      'Çmimi Shitjes': p.sale_price,
      'Stoku': p.stock,
      'Stoku Min': p.min_stock || 5,
      'Përshkrimi': p.description || '',
      'Dyqani': getStoreName(p.store_id)
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `produktet_${storeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${products.length} produkte u eksportuan`);
  };

  // Handle CSV file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setImportPreview(jsonData.slice(0, 10)); // Preview first 10
        setIsImportModalOpen(true);
      } catch (error) {
        toast.error('Gabim në leximin e skedarit');
      }
    };
    reader.readAsBinaryString(file);
  };

  // Import products from CSV
  const importProductsCSV = async () => {
    if (!importFile || !currentStore?.id) {
      toast.error('Zgjidhni dyqanin dhe skedarin');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let imported = 0;
        let errors = 0;

        for (const row of jsonData) {
          try {
            // Find category by name or create new
            let categoryId = '';
            const categoryName = row['Kategoria'] || row['kategoria'] || row['Category'];
            if (categoryName) {
              const existingCat = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
              if (existingCat) {
                categoryId = existingCat.id;
              }
            }

            const product = {
              name: row['Emri'] || row['emri'] || row['Name'] || row['name'] || '',
              barcode: String(row['Barkodi'] || row['barkodi'] || row['Barcode'] || row['barcode'] || ''),
              category_id: categoryId,
              purchase_price: parseFloat(row['Çmimi Blerjes'] || row['cmimi_blerjes'] || row['Purchase Price'] || 0),
              sale_price: parseFloat(row['Çmimi Shitjes'] || row['cmimi_shitjes'] || row['Sale Price'] || 0),
              stock: parseInt(row['Stoku'] || row['stoku'] || row['Stock'] || 0),
              min_stock: parseInt(row['Stoku Min'] || row['stoku_min'] || row['Min Stock'] || 5),
              description: row['Përshkrimi'] || row['pershkrimi'] || row['Description'] || '',
              store_id: currentStore.id
            };

            if (product.name) {
              await api.post('/products', product);
              imported++;
            }
          } catch (err) {
            errors++;
            console.error('Import error:', err);
          }
        }

        toast.success(`${imported} produkte u importuan${errors > 0 ? `, ${errors} gabime` : ''}`);
        setIsImportModalOpen(false);
        setImportFile(null);
        setImportPreview([]);
        loadAllData();
      };
      reader.readAsBinaryString(importFile);
    } catch (error) {
      toast.error('Gabim në importimin e produkteve');
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = [
      { 'Emri': 'Produkt Shembull', 'Barkodi': '1234567890', 'Kategoria': 'Elektronikë', 'Çmimi Blerjes': 100, 'Çmimi Shitjes': 150, 'Stoku': 10, 'Stoku Min': 5, 'Përshkrimi': 'Përshkrim i produktit' }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_produktet.csv';
    link.click();
    toast.info('Template u shkarkua');
  };

  useEffect(() => { loadAllData(); }, [currentStore]);

  // Handle query params for direct access
  useEffect(() => {
    if (!loading && products.length > 0) {
      const productId = searchParams.get('product_id');
      const action = searchParams.get('action');

      if (productId && action === 'edit') {
        const product = products.find(p => p.id === productId);
        if (product) {
          // Switch to products tab if not active
          if (activeTab !== 'products') setActiveTab('products');
          openProductEdit(product);
          // Optional: clear params or leave them
        }
      }
    }
  }, [loading, products, searchParams, activeTab]);

  const loadAllData = async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [productsRes, categoriesRes, suppliersRes, adjustmentsRes] = await Promise.all([
        api.get(`/products${storeParam}`),
        api.get(`/categories${storeParam}`),
        api.get(`/suppliers${storeParam}`),
        api.get(`/inventory${storeParam}`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data);
      setAdjustments(adjustmentsRes.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.barcode.includes(productSearch);
    const matchesCategory = categoryFilter === 'all' || p.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  const filteredCategories = categories.filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase()));
  const filteredSuppliers = suppliers.filter(s => s.name.toLowerCase().includes(supplierSearch.toLowerCase()) || s.phone?.includes(supplierSearch));
  const filteredInventory = products.filter(p => p.name.toLowerCase().includes(inventorySearch.toLowerCase()) || p.barcode.includes(inventorySearch));

  // Stats
  const lowStockProducts = products.filter(p => p.stock <= p.min_stock);
  const outOfStockProducts = products.filter(p => p.stock <= 0);
  const totalStockValue = products.reduce((sum, p) => sum + (p.stock * p.purchase_price), 0);
  const totalSupplierDebt = suppliers.reduce((sum, s) => sum + (s.debt || 0), 0);

  // Scanner functions
  const startScanner = async () => {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Shfletuesi juaj nuk e mbështet kamerën. Përdorni Chrome ose Firefox.');
      return;
    }

    setIsScannerOpen(true);

    setTimeout(async () => {
      if (scannerRef.current && !html5QrcodeScannerRef.current) {
        try {
          // Test camera access first
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          stream.getTracks().forEach(track => track.stop());

          html5QrcodeScannerRef.current = new Html5QrcodeScanner(
            "barcode-scanner-magazina",
            {
              fps: 10,
              qrbox: { width: 250, height: 100 },
              aspectRatio: 2.0,
              showTorchButtonIfSupported: true,
              showZoomSliderIfSupported: true
            },
            false
          );
          html5QrcodeScannerRef.current.render(onScanSuccess, onScanFailure);
          toast.success('Kamera u hap. Vendosni barkodin para kamerës.');
        } catch (err) {
          console.error("Camera error:", err);
          let errorMsg = 'Nuk mund të hapet kamera.';

          if (err.name === 'NotAllowedError') {
            errorMsg = 'Leja për kamerën u refuzua. Lejoni aksesin në cilësimet.';
          } else if (err.name === 'NotFoundError') {
            errorMsg = 'Nuk u gjet asnjë kamerë në këtë pajisje.';
          } else if (err.name === 'NotReadableError') {
            errorMsg = 'Kamera po përdoret nga një aplikacion tjetër.';
          }

          toast.error(errorMsg);
          setIsScannerOpen(false);
        }
      }
    }, 100);
  };

  const stopScanner = () => {
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear().catch(console.error);
      html5QrcodeScannerRef.current = null;
    }
    setIsScannerOpen(false);
  };

  const onScanSuccess = (decodedText) => {
    const product = products.find(p => p.barcode === decodedText);
    if (product) {
      stopScanner();
      setSelectedProduct(product);
      setNewStock(product.stock);
      setIsScannedProductOpen(true);
      toast.success(`Produkti u gjet: ${product.name}`);
    } else {
      toast.error(`Produkti me barcode "${decodedText}" nuk u gjet`);
    }
  };

  const onScanFailure = (error) => {
    // Ignore scan failures silently
  };

  const handleQuickStockUpdate = async (adjustment) => {
    const newStockValue = selectedProduct.stock + adjustment;
    if (newStockValue < 0) {
      toast.error('Stoku nuk mund të jetë negativ');
      return;
    }
    try {
      await api.post('/inventory', {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        old_stock: selectedProduct.stock,
        new_stock: newStockValue,
        difference: adjustment,
        reason: adjustment > 0 ? 'Shtim nga skanimi' : 'Heqje nga skanimi'
      });
      toast.success(`Stoku u ${adjustment > 0 ? 'shtua' : 'ul'} me ${Math.abs(adjustment)}`);
      loadAllData();
      // Update selected product locally
      setSelectedProduct({ ...selectedProduct, stock: newStockValue });
    } catch (error) {
      toast.error('Gabim në përditësimin e stokut');
    }
  };

  // Product handlers
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = { ...productForm };
      if (currentStore?.id) {
        productData.store_id = currentStore.id;
      }
      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}`, productData);
        toast.success('Produkti u përditësua');
      } else {
        await api.post('/products', productData);
        toast.success('Produkti u shtua');
      }
      setIsProductModalOpen(false);
      resetProductForm();
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ruajtje');
    }
  };

  const handleProductDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produkti u fshi');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const resetProductForm = () => {
    setProductForm({ name: '', barcode: '', category_id: '', purchase_price: 0, sale_price: 0, stock: 0, min_stock: 5, description: '' });
    setSelectedProduct(null);
  };

  const openProductEdit = (product) => {
    setSelectedProduct(product);
    setProductForm({ name: product.name, barcode: product.barcode, category_id: product.category_id || '', purchase_price: product.purchase_price, sale_price: product.sale_price, stock: product.stock, min_stock: product.min_stock, description: product.description || '' });
    setIsProductModalOpen(true);
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryData = { ...categoryForm };
      if (currentStore?.id) {
        categoryData.store_id = currentStore.id;
      }
      if (selectedCategory) {
        await api.put(`/categories/${selectedCategory.id}`, categoryData);
        toast.success('Kategoria u përditësua');
      } else {
        await api.post('/categories', categoryData);
        toast.success('Kategoria u shtua');
      }
      setIsCategoryModalOpen(false);
      resetCategoryForm();
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Kategoria u fshi');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '' });
    setSelectedCategory(null);
  };

  const openCategoryEdit = (category) => {
    setSelectedCategory(category);
    setCategoryForm({ name: category.name, description: category.description || '' });
    setIsCategoryModalOpen(true);
  };

  // Supplier handlers
  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      const supplierData = { ...supplierForm };
      if (currentStore?.id) {
        supplierData.store_id = currentStore.id;
      }
      if (selectedSupplier) {
        await api.put(`/suppliers/${selectedSupplier.id}`, supplierData);
        toast.success('Furnitori u përditësua');
      } else {
        await api.post('/suppliers', supplierData);
        toast.success('Furnitori u shtua');
      }
      setIsSupplierModalOpen(false);
      resetSupplierForm();
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleSupplierDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('Furnitori u fshi');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const resetSupplierForm = () => {
    setSupplierForm({ name: '', phone: '', email: '', address: '', debt: 0 });
    setSelectedSupplier(null);
  };

  const openSupplierEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierForm({ name: supplier.name, phone: supplier.phone || '', email: supplier.email || '', address: supplier.address || '', debt: supplier.debt });
    setIsSupplierModalOpen(true);
  };

  const handleSupplierPayment = async () => {
    if (paymentAmount <= 0) {
      toast.error('Shuma duhet të jetë më e madhe se 0');
      return;
    }
    try {
      await api.post('/debt-payments', { entity_type: 'supplier', entity_id: selectedSupplier.id, entity_name: selectedSupplier.name, amount: paymentAmount, notes: 'Pagesë borxhi' });
      toast.success('Pagesa u regjistrua');
      setIsPaymentModalOpen(false);
      setPaymentAmount(0);
      loadAllData();
    } catch (error) {
      toast.error('Gabim në regjistrimin e pagesës');
    }
  };

  // Inventory handlers
  const handleInventoryAdjustment = async () => {
    if (!reason.trim()) {
      toast.error('Shkruaj arsyen e rregullimit');
      return;
    }
    try {
      await api.post('/inventory', { product_id: selectedProduct.id, product_name: selectedProduct.name, old_stock: selectedProduct.stock, new_stock: newStock, difference: newStock - selectedProduct.stock, reason });
      toast.success('Stoku u rregullua');
      setIsInventoryModalOpen(false);
      setReason('');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const openInventoryModal = (product) => {
    setSelectedProduct(product);
    setNewStock(product.stock);
    setIsInventoryModalOpen(true);
  };

  // Barcode handlers
  const openBarcodeModal = (product) => {
    setSelectedProduct(product);
    setIsBarcodeModalOpen(true);
  };

  const printBarcode = () => {
    const printWindow = window.open('', '_blank', 'width=400,height=300');
    const labels = Array(labelCount).fill(null).map((_, i) => `
      <div class="label">
        <div class="product-name">${selectedProduct?.name}</div>
        <svg id="barcode-${i}"></svg>
        <div class="price">${formatCurrency(selectedProduct?.sale_price)}</div>
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Etiketa - ${selectedProduct?.name}</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            @page { size: 40mm 30mm; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; }
            .label { width: 40mm; height: 30mm; padding: 2mm; display: flex; flex-direction: column; align-items: center; justify-content: center; page-break-after: always; border: 1px dashed #ccc; }
            .label:last-child { page-break-after: auto; }
            .product-name { font-size: 8px; font-weight: bold; text-align: center; max-width: 36mm; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 1mm; }
            svg { max-width: 36mm; height: 12mm; }
            .price { font-size: 12px; font-weight: bold; margin-top: 1mm; }
            @media print { .label { border: none; } }
          </style>
        </head>
        <body>
          ${labels}
          <script>
            window.onload = function() {
              ${Array(labelCount).fill(null).map((_, i) => `
                JsBarcode("#barcode-${i}", "${selectedProduct?.barcode}", { format: "CODE128", width: 1.5, height: 30, displayValue: true, fontSize: 10, margin: 0, textMargin: 0 });
              `).join('')}
              setTimeout(function() { window.print(); }, 200);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Helper functions
  const getCategoryName = (categoryId) => categories.find(c => c.id === categoryId)?.name || '-';
  const getStockBadge = (product) => {
    if (product.stock <= 0) return <Badge variant="destructive" className="font-mono">Pa stok</Badge>;
    if (product.stock <= product.min_stock) return <Badge variant="warning" className="font-mono bg-orange-100 text-orange-800">{product.stock}</Badge>;
    return <Badge variant="secondary" className="font-mono">{product.stock}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="magazina-page">
      {/* Scanner Button - Fixed Position */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800">Magazina</h1>
        <Button
          onClick={startScanner}
          variant="outline"
          className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
        >
          <Camera className="w-5 h-5" />
          Skano Barcode
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="products"><Package className="w-4 h-4 mr-2" />Produktet</TabsTrigger>
          <TabsTrigger value="categories"><FolderOpen className="w-4 h-4 mr-2" />Kategorit</TabsTrigger>
          <TabsTrigger value="inventory"><ClipboardList className="w-4 h-4 mr-2" />Inventari</TabsTrigger>
          <TabsTrigger value="suppliers"><Truck className="w-4 h-4 mr-2" />Furnitorët</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Kërko produkte..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40"><SelectValue placeholder="Kategoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Të gjitha</SelectItem>
                  {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportProductsCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />Eksporto CSV
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-csv-btn">
                <Upload className="w-4 h-4 mr-2" />Importo CSV
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.xlsx,.xls"
                className="hidden"
              />
              <Button onClick={() => { resetProductForm(); setIsProductModalOpen(true); }}><Plus className="w-4 h-4 mr-2" />Produkt i Ri</Button>
            </div>
          </div>

          {lowStockProducts.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="py-3">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">{lowStockProducts.length} produkte me stok të ulët</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showStoreColumn && <TableHead>Dyqani</TableHead>}
                    <TableHead>Produkti</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>Kategoria</TableHead>
                    <TableHead className="text-right">Çmimi Blerjes</TableHead>
                    <TableHead className="text-right">Çmimi Shitjes</TableHead>
                    <TableHead className="text-center">Stoku</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.length === 0 ? (
                    <TableRow><TableCell colSpan={showStoreColumn ? 8 : 7} className="text-center py-8 text-slate-500"><Package className="w-12 h-12 mx-auto mb-2 opacity-20" />Nuk u gjet asnjë produkt</TableCell></TableRow>
                  ) : (
                    filteredProducts.map(product => (
                      <TableRow key={product.id}>
                        {showStoreColumn && <TableCell><Badge variant="outline" className="text-xs">{getStoreName(product.store_id)}</Badge></TableCell>}
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-sm">{product.barcode}</TableCell>
                        <TableCell>{getCategoryName(product.category_id)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(product.purchase_price)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(product.sale_price)}</TableCell>
                        <TableCell className="text-center">{getStockBadge(product)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openBarcodeModal(product)}><Barcode className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openProductEdit(product)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleProductDelete(product.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Kërko kategori..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={() => { resetCategoryForm(); setIsCategoryModalOpen(true); }}><Plus className="w-4 h-4 mr-2" />Kategori e Re</Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Emri</TableHead>
                    <TableHead>Përshkrimi</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500"><FolderOpen className="w-12 h-12 mx-auto mb-2 opacity-20" />Nuk ka kategori</TableCell></TableRow>
                  ) : (
                    filteredCategories.map(category => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell className="text-slate-500">{category.description || '-'}</TableCell>
                        <TableCell className="text-slate-500">{formatDate(category.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openCategoryEdit(category)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCategoryDelete(category.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Total Produktesh</p><p className="text-2xl font-bold">{products.length}</p></div><ClipboardList className="w-8 h-8 text-primary opacity-50" /></div></CardContent></Card>
            <Card><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Vlera e Stokut</p><p className="text-2xl font-bold font-mono">{formatCurrency(totalStockValue)}</p></div><ClipboardList className="w-8 h-8 text-lime-600 opacity-50" /></div></CardContent></Card>
            <Card className={lowStockProducts.length > 0 ? 'border-orange-200' : ''}><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Stok i Ulët</p><p className="text-2xl font-bold text-orange-600">{lowStockProducts.length}</p></div><AlertTriangle className="w-8 h-8 text-orange-500 opacity-50" /></div></CardContent></Card>
            <Card className={outOfStockProducts.length > 0 ? 'border-rose-200' : ''}><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Pa Stok</p><p className="text-2xl font-bold text-rose-600">{outOfStockProducts.length}</p></div><AlertTriangle className="w-8 h-8 text-rose-500 opacity-50" /></div></CardContent></Card>
          </div>

          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Kërko produkte..." value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} className="pl-10" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Stoku i Produkteve</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Produkti</TableHead><TableHead className="text-center">Stoku</TableHead><TableHead className="text-center">Min</TableHead><TableHead className="text-right">Veprime</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredInventory.map(product => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={product.stock <= 0 ? 'destructive' : product.stock <= product.min_stock ? 'warning' : 'secondary'} className={product.stock <= 0 ? '' : product.stock <= product.min_stock ? 'bg-orange-100 text-orange-800' : ''}>{product.stock}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-mono text-sm text-slate-500">{product.min_stock}</TableCell>
                          <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => openInventoryModal(product)}>Rregulloni</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Rregullimet e Fundit</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Produkti</TableHead><TableHead className="text-center">Ndryshimi</TableHead><TableHead>Arsyeja</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {adjustments.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Nuk ka rregullime</TableCell></TableRow>
                      ) : (
                        adjustments.slice(0, 20).map(adj => (
                          <TableRow key={adj.id}>
                            <TableCell className="font-mono text-xs">{formatDateTime(adj.created_at)}</TableCell>
                            <TableCell className="font-medium text-sm">{adj.product_name}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                {adj.difference > 0 ? <ArrowUp className="w-4 h-4 text-lime-600" /> : <ArrowDown className="w-4 h-4 text-rose-600" />}
                                <span className={`font-mono font-bold ${adj.difference > 0 ? 'text-lime-600' : 'text-rose-600'}`}>{adj.difference > 0 ? '+' : ''}{adj.difference}</span>
                              </div>
                              <span className="text-xs text-slate-400">{adj.old_stock} → {adj.new_stock}</span>
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">{adj.reason}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Kërko furnitorë..." value={supplierSearch} onChange={(e) => setSupplierSearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={() => { resetSupplierForm(); setIsSupplierModalOpen(true); }}><Plus className="w-4 h-4 mr-2" />Furnitor i Ri</Button>
          </div>

          {totalSupplierDebt > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <span className="text-orange-800 font-medium">Borxh total ndaj furnitorëve:</span>
                  <span className="text-orange-800 font-mono font-bold">{formatCurrency(totalSupplierDebt)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Emri</TableHead>
                    <TableHead>Telefoni</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Adresa</TableHead>
                    <TableHead className="text-right">Borxhi</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSuppliers.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500"><Truck className="w-12 h-12 mx-auto mb-2 opacity-20" />Nuk u gjet asnjë furnitor</TableCell></TableRow>
                  ) : (
                    filteredSuppliers.map(supplier => (
                      <TableRow key={supplier.id}>
                        <TableCell className="font-medium">{supplier.name}</TableCell>
                        <TableCell>{supplier.phone && <span className="flex items-center gap-1 text-sm"><Phone className="w-3 h-3" />{supplier.phone}</span>}</TableCell>
                        <TableCell>{supplier.email && <span className="flex items-center gap-1 text-sm"><Mail className="w-3 h-3" />{supplier.email}</span>}</TableCell>
                        <TableCell>{supplier.address && <span className="flex items-center gap-1 text-sm"><MapPin className="w-3 h-3" />{supplier.address}</span>}</TableCell>
                        <TableCell className="text-right">
                          {supplier.debt > 0 ? <Badge variant="warning" className="font-mono bg-orange-100 text-orange-800">{formatCurrency(supplier.debt)}</Badge> : <span className="text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {supplier.debt > 0 && <Button variant="ghost" size="sm" onClick={() => { setSelectedSupplier(supplier); setPaymentAmount(supplier.debt); setIsPaymentModalOpen(true); }}><CreditCard className="w-4 h-4 text-lime-600" /></Button>}
                            <Button variant="ghost" size="sm" onClick={() => openSupplierEdit(supplier)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleSupplierDelete(supplier.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Modal */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selectedProduct ? 'Modifiko Produktin' : 'Produkt i Ri'}</DialogTitle></DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Emri</Label><Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required /></div>
              <div className="col-span-2"><Label>Barcode</Label><div className="flex gap-2"><Input value={productForm.barcode} onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })} required className="font-mono" /><Button type="button" variant="outline" onClick={() => setProductForm({ ...productForm, barcode: generateBarcode() })}><RefreshCw className="w-4 h-4" /></Button></div></div>
              <div className="col-span-2"><Label>Kategoria</Label><Select value={productForm.category_id} onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}><SelectTrigger><SelectValue placeholder="Zgjidh kategorinë" /></SelectTrigger><SelectContent>{categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Çmimi Blerjes (LEK)</Label><NumericInput value={productForm.purchase_price} onChange={(e) => setProductForm({ ...productForm, purchase_price: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
              <div><Label>Çmimi Shitjes (LEK)</Label><NumericInput value={productForm.sale_price} onChange={(e) => setProductForm({ ...productForm, sale_price: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
              <div><Label>Stoku</Label><NumericInput value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) || 0 })} className="font-mono" /></div>
              <div><Label>Stoku Minimal</Label><NumericInput value={productForm.min_stock} onChange={(e) => setProductForm({ ...productForm, min_stock: parseInt(e.target.value) || 0 })} className="font-mono" /></div>
              <div className="col-span-2"><Label>Përshkrimi</Label><Input value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsProductModalOpen(false)}>Anulo</Button><Button type="submit">Ruaj</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedCategory ? 'Modifiko Kategorinë' : 'Kategori e Re'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div><Label>Emri *</Label><Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required /></div>
            <div><Label>Përshkrimi</Label><Input value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsCategoryModalOpen(false)}>Anulo</Button><Button type="submit">Ruaj</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Modal */}
      <Dialog open={isSupplierModalOpen} onOpenChange={setIsSupplierModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedSupplier ? 'Modifiko Furnitorin' : 'Furnitor i Ri'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSupplierSubmit} className="space-y-4">
            <div><Label>Emri *</Label><Input value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} required /></div>
            <div><Label>Telefoni</Label><Input value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} /></div>
            <div><Label>Adresa</Label><Input value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsSupplierModalOpen(false)}>Anulo</Button><Button type="submit">Ruaj</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inventory Adjustment Modal */}
      <Dialog open={isInventoryModalOpen} onOpenChange={setIsInventoryModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rregullim Stoku</DialogTitle></DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="text-center py-2">
                <p className="text-slate-500">Produkti</p>
                <p className="text-lg font-semibold">{selectedProduct.name}</p>
                <p className="text-slate-500 mt-2">Stoku aktual</p>
                <p className="text-3xl font-mono font-bold">{selectedProduct.stock}</p>
              </div>
              <div><Label>Stoku i ri</Label><NumericInput value={newStock} onChange={(e) => setNewStock(parseInt(e.target.value) || 0)} className="font-mono text-xl text-center" /></div>
              {newStock !== selectedProduct.stock && (
                <div className={`p-3 rounded-lg text-center ${newStock > selectedProduct.stock ? 'bg-lime-50 text-lime-800' : 'bg-rose-50 text-rose-800'}`}>
                  <p className="font-mono font-bold text-lg">{newStock > selectedProduct.stock ? '+' : ''}{newStock - selectedProduct.stock}</p>
                </div>
              )}
              <div><Label>Arsyeja *</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger><SelectValue placeholder="Zgjidh arsyen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Numërim inventari">Numërim inventari</SelectItem>
                    <SelectItem value="Gabim sistemi">Gabim sistemi</SelectItem>
                    <SelectItem value="Produkt i dëmtuar">Produkt i dëmtuar</SelectItem>
                    <SelectItem value="Kthim nga klienti">Kthim nga klienti</SelectItem>
                    <SelectItem value="Humbje/Vjedhje">Humbje/Vjedhje</SelectItem>
                    <SelectItem value="Rregullim tjetër">Rregullim tjetër</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setIsInventoryModalOpen(false)}>Anulo</Button><Button onClick={handleInventoryAdjustment} disabled={newStock === selectedProduct?.stock || !reason}>Ruaj</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barcode Modal */}
      <Dialog open={isBarcodeModalOpen} onOpenChange={setIsBarcodeModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Barcode - {selectedProduct?.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center py-4">
            {selectedProduct && <BarcodeDisplay code={selectedProduct.barcode} />}
            <p className="text-lg font-mono font-bold mt-2">{formatCurrency(selectedProduct?.sale_price || 0)}</p>
          </div>
          <div className="flex items-center justify-center gap-4 py-2">
            <Label>Sasia e etiketave:</Label>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setLabelCount(Math.max(1, labelCount - 1))}>-</Button>
              <Input type="number" value={labelCount} onChange={(e) => setLabelCount(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center font-mono" min={1} max={100} />
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setLabelCount(labelCount + 1)}>+</Button>
            </div>
          </div>
          <div className="flex gap-2 justify-center">
            {[1, 5, 10, 20, 50].map(count => <Button key={count} variant={labelCount === count ? 'default' : 'outline'} size="sm" onClick={() => setLabelCount(count)}>{count}</Button>)}
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setIsBarcodeModalOpen(false)}>Mbyll</Button><Button onClick={printBarcode}><Printer className="w-4 h-4 mr-2" />Printo {labelCount} Etiketa</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pagesë Borxhi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-slate-500">Furnitori</p>
              <p className="text-xl font-semibold">{selectedSupplier?.name}</p>
              <p className="text-slate-500 mt-2">Borxhi aktual</p>
              <p className="text-2xl font-mono font-bold text-orange-600">{formatCurrency(selectedSupplier?.debt || 0)}</p>
            </div>
            <div><Label>Shuma e pagesës (LEK)</Label><NumericInput value={paymentAmount} onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)} max={selectedSupplier?.debt || 0} className="font-mono text-lg" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>Anulo</Button><Button onClick={handleSupplierPayment}>Regjistro Pagesën</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scanner Modal */}
      <Dialog open={isScannerOpen} onOpenChange={(open) => { if (!open) stopScanner(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-primary" />
              Skano Barcode për Kontroll Stoku
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              id="barcode-scanner-magazina"
              ref={scannerRef}
              className="w-full min-h-[300px] bg-slate-100 rounded-lg overflow-hidden"
            />
            <p className="text-sm text-slate-500 text-center">
              Vendos barcode-in përpara kamerës për të kontrolluar stokun
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={stopScanner}>
              <X className="w-4 h-4 mr-2" />Mbyll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scanned Product Modal */}
      <Dialog open={isScannedProductOpen} onOpenChange={setIsScannedProductOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Detajet e Produktit
            </DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="text-center py-4 bg-slate-50 rounded-lg">
                <p className="text-xl font-bold">{selectedProduct.name}</p>
                <p className="text-sm text-slate-500 font-mono">{selectedProduct.barcode}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Çmimi Blerjes</p>
                  <p className="text-lg font-mono font-bold">{formatCurrency(selectedProduct.purchase_price)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Çmimi Shitjes</p>
                  <p className="text-lg font-mono font-bold text-primary">{formatCurrency(selectedProduct.sale_price)}</p>
                </div>
              </div>

              <div className={`p-4 rounded-lg text-center ${selectedProduct.stock <= 0 ? 'bg-rose-100' :
                selectedProduct.stock <= selectedProduct.min_stock ? 'bg-orange-100' : 'bg-lime-100'
                }`}>
                <p className="text-sm text-slate-600">Stoku Aktual</p>
                <p className={`text-4xl font-mono font-bold ${selectedProduct.stock <= 0 ? 'text-rose-600' :
                  selectedProduct.stock <= selectedProduct.min_stock ? 'text-orange-600' : 'text-lime-600'
                  }`}>{selectedProduct.stock}</p>
                <p className="text-xs text-slate-500 mt-1">Min: {selectedProduct.min_stock}</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 text-center">Rregullim i Shpejtë</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(-10)} className="font-mono">-10</Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(-5)} className="font-mono">-5</Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(-1)} className="font-mono">-1</Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(1)} className="font-mono text-lime-600">+1</Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(5)} className="font-mono text-lime-600">+5</Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickStockUpdate(10)} className="font-mono text-lime-600">+10</Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setIsScannedProductOpen(false); startScanner(); }}>
              <Camera className="w-4 h-4 mr-2" />Skano Tjetër
            </Button>
            <Button onClick={() => setIsScannedProductOpen(false)}>Mbyll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Importo Produktet nga CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!currentStore?.id && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-800 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Zgjidhni një dyqan specifik për të importuar produktet
              </div>
            )}

            {importPreview.length > 0 && (
              <>
                <p className="text-sm text-slate-600">
                  Paraqitja e {importPreview.length} produkteve të para:
                </p>
                <div className="max-h-64 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(importPreview[0]).slice(0, 5).map((key) => (
                          <TableHead key={key} className="text-xs">{key}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.map((row, idx) => (
                        <TableRow key={idx}>
                          {Object.values(row).slice(0, 5).map((val, i) => (
                            <TableCell key={i} className="text-xs py-1">{String(val).substring(0, 30)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />Shkarko Template
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsImportModalOpen(false); setImportFile(null); setImportPreview([]); }}>
              Anulo
            </Button>
            <Button
              onClick={importProductsCSV}
              disabled={importing || !currentStore?.id || importPreview.length === 0}
            >
              {importing ? 'Duke importuar...' : `Importo në ${currentStore?.name || 'Dyqan'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
